import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CadScaleDialogProps {
  pixelDistance: number;
  onConfirm: (realDistanceMeters: number) => void;
  onCancel: () => void;
}

type DistanceUnit = 'm' | 'cm' | 'mm';

export function CadScaleDialog({ pixelDistance: _pixelDistance, onConfirm, onCancel }: CadScaleDialogProps) {
  void _pixelDistance; // available for future display use
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState<DistanceUnit>('m');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleConfirm = useCallback(() => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= 0) return;

    let meters: number;
    switch (unit) {
      case 'mm':
        meters = num / 1000;
        break;
      case 'cm':
        meters = num / 100;
        break;
      default:
        meters = num;
    }
    onConfirm(meters);
  }, [value, unit, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirm();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [handleConfirm, onCancel],
  );

  const isValid = !isNaN(parseFloat(value)) && parseFloat(value) > 0;

  return (
    <div className="absolute bottom-[80px] left-1/2 -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="bg-[#1E1E1E] border border-white/15 rounded-xl p-4 shadow-2xl min-w-[320px]">
        <div className="text-sm text-gray-300 mb-3 font-medium">
          {t('projects.cadProject.setScalePrompt', 'Enter real-world distance between the two points:')}
        </div>

        <div className="flex items-center gap-2 mb-3">
          <input
            ref={inputRef}
            type="number"
            step="any"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="0.00"
            className="flex-1 h-9 bg-[#0a0e14] border border-white/15 rounded-lg px-3 text-white text-sm outline-none focus:border-[#d4ff33]/50 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as DistanceUnit)}
            className="h-9 bg-[#0a0e14] border border-white/15 rounded-lg px-2 text-white text-sm outline-none cursor-pointer"
          >
            <option value="m">m</option>
            <option value="cm">cm</option>
            <option value="mm">mm</option>
          </select>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              isValid ? 'bg-[#d4ff33] text-black hover:bg-[#e0ff66]' : 'bg-white/5 text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('common.confirm', 'Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
